import React from "react";
import { FaChevronDown, FaChevronRight, FaFolder, FaFolderOpen } from "react-icons/fa6";
import ActionPopupMenu from "../Common/ActionPopupMenu";
import { useSelector } from "react-redux";

/**
 * Recursive folder-tree node used inside the "Folder Structure" panel.
 * Kept purely presentational — all mutations flow through the callbacks
 * provided by the parent component.
 */
const FolderTreeNode = ({
  node,
  depth = 0,
  activeFolderId,
  onSelectFolder,
  onRenameFolder,
  onDeleteFolder,
  onManageMembers,
  expandedFolderIds,
  toggleFolderExpanded,
  isSorted = false,
  permissions = { isView: true, isSave: true, isDelete: true },
}) => {
  const { user } = useSelector((state) => state.root.auth);
  const nodeId = node.folderDNSID ?? node.folderID ?? node.id;
  const nodeName = node.folderName ?? node.name;
  const isExpanded = expandedFolderIds.has(nodeId);
  const hasChildren = node.children?.length > 0;
  const isActive =
    activeFolderId !== undefined &&
    activeFolderId !== null &&
    activeFolderId === nodeId;

  const sortedChildren =
    isSorted && node.children
      ? [...node.children].sort((a, b) => {
        const nameA = (a.folderName ?? a.name ?? "").toLowerCase();
        const nameB = (b.folderName ?? b.name ?? "").toLowerCase();
        return nameA.localeCompare(nameB);
      })
      : node.children;

  const folderMenuItems = [
    {
      icon: <span className="text-lg">📂</span>,
      label: "Open",
      onClick: () => onSelectFolder(node),
    },
    permissions?.isSave && {
      icon: <span className="text-lg">✏️</span>,
      label: "Rename",
      onClick: () => onRenameFolder(nodeId, nodeName, "Folder"),
    },
    permissions?.isSave && {
      icon: <span className="text-lg">↔️</span>,
      label: "Move",
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent("openMoveModal", {
            detail: { id: nodeId, name: nodeName, type: "Folder", isCopy: false },
          })
        );
      },
    },
    permissions?.isSave && {
      icon: <span className="text-lg">📋</span>,
      label: "Copy",
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent("openMoveModal", {
            detail: { id: nodeId, name: nodeName, type: "Folder", isCopy: true },
          })
        );
      },
    },
    // typeof onManageMembers === "function" &&
    // node?.parentId === 0 &&
    (user?.parentID === 0 && user?.userID === node?.userID) && {
      icon: <span className="text-lg">👥</span>,
      label: "Manage Members",
      onClick: () => onManageMembers(node),
    },
    permissions?.isDelete && { divider: true },
    permissions?.isDelete && {
      icon: <span className="text-lg">🗑️</span>,
      label: "Delete",
      isDelete: true,
      onClick: () => onDeleteFolder(nodeId, nodeName, "Folder"),
    },
  ].filter(Boolean);

  return (
    <div>
      <div
        className={`group flex items-center gap-1 pr-2 py-2 cursor-pointer rounded-md transition-colors ${isActive
          ? "bg-indigo-50 text-indigo-700 font-semibold"
          : "hover:bg-slate-50 text-slate-700"
          }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => {
          if (hasChildren) toggleFolderExpanded(nodeId);
          onSelectFolder(node);
        }}
      >
        <span className="w-3.5 text-slate-400 shrink-0">
          {hasChildren ? (
            isExpanded ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />
          ) : null}
        </span>
        {isExpanded && hasChildren ? (
          <FaFolderOpen size={13} className="text-indigo-400 shrink-0" />
        ) : (
          <FaFolder
            size={13}
            className={`${isActive ? "text-indigo-500" : "text-slate-400"} shrink-0`}
          />
        )}
        <span
          className={`text-xs flex-1 truncate ${isActive
            ? "text-indigo-700 font-semibold"
            : "text-slate-700 font-medium"
            }`}
        >
          {nodeName}
        </span>
        {folderMenuItems.length > 0 && (
          <ActionPopupMenu
            placement="right-start"
            alwaysShowTrigger={isActive}
            className="w-52"
            items={folderMenuItems}
          />
        )}
      </div>
      {isExpanded && hasChildren && (
        <div>
          {sortedChildren.map((child) => (
            <FolderTreeNode
              key={child.folderDNSID ?? child.folderID ?? child.id}
              node={child}
              depth={depth + 1}
              activeFolderId={activeFolderId}
              onSelectFolder={onSelectFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onManageMembers={onManageMembers}
              expandedFolderIds={expandedFolderIds}
              toggleFolderExpanded={toggleFolderExpanded}
              isSorted={isSorted}
              permissions={permissions}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FolderTreeNode;
